/**
 * Client-side Entity Registry
 *
 * Dispatch path for `executionMode: 'client-side'` entities — capabilities
 * that execute entirely in the user's browser (e.g. WASM models) and never
 * reach the server. This is the client-side analog of the server's
 * `invokeEntity()` gateway: same "every capability is an entity" promise,
 * different transport.
 *
 * Two callers:
 *   - Internal arches import `invokeClientEntity` and call it directly.
 *   - Plugin arches go through the bridge's `client_entity_invoke` op,
 *     which resolves the entity registration (for permission + per-plugin
 *     rate limit) and then calls `invokeClientEntity` on the plugin's
 *     behalf.
 *
 * Executor definitions live in `@fias/client-entities`
 * (web-packages/client-entities); `client/src/lib/register-client-entities.ts`
 * registers them at startup via
 * `registerClientEntity(entityId, registration)`. The registration carries
 * the rate-limit cap and the plugin permission scope — duplicates of the
 * canonical entity config fields, mirrored there to avoid pulling
 * `@fias/entities` (and its server-only transitive deps) into the client
 * bundle. The architecture test
 * `client-entity-registry-mirrors-entity-config.test.ts` enforces that
 * each definition agrees with the entity config.
 *
 * Rate limit: per-`(userId, archeId, entityId)` token bucket, sourced from
 * the registration's `rateLimitPerMinute` (20/min for NLLB). Applied to ALL
 * callers (internal and plugin). The bridge separately enforces a generic
 * ~60/min cap across *all* ops; for a plugin hammering a single entity the
 * registry's per-entity 20/min binds *first*, so this is not redundant — it's
 * the tighter, per-entity limit. For the internal path (no bridge) it's the
 * only guard.
 *
 * This is client-side, in-memory, and per-tab: it's DoS self-protection
 * (WASM inference is CPU-heavy), NOT a security boundary. That's acceptable
 * here because the work is 100% on-device — there is no server round-trip,
 * billing, or provider cost for a bypass to abuse.
 */

import type {
  ClientEntityDefinition,
  ClientEntityExecutor,
  ClientEntityInvocationContext,
} from '@fias/client-entities';

/**
 * The registry's public types are DERIVED from `@fias/client-entities`'
 * `ClientEntityDefinition` — the single source of truth for the client-entity
 * shape — rather than re-declared here. `@fias/client-entities` is a
 * browser-safe leaf package (no `@fias/entities` / server transitive deps), so
 * importing its TYPES carries none of the coupling the literal value-mirroring
 * exists to avoid, while closing the structural-only seam: a new field on the
 * definition now flows into the registration type automatically instead of
 * being able to drift silently past the compiler. (The value literals — rate
 * limit, permission — are still mirrored and pinned against the entity catalog
 * by client-entity-registry-mirrors-entity-config.test.ts.)
 */
export type ClientEntityContext = ClientEntityInvocationContext;
export type { ClientEntityExecutor };

/**
 * What the registry stores for an entity: a `ClientEntityDefinition` minus its
 * `entityId` (the registry keys on the id separately in
 * `registerClientEntity(entityId, registration)`).
 */
export type ClientEntityRegistration<TInput = unknown, TResult = unknown> = Omit<
  ClientEntityDefinition<TInput, TResult>,
  'entityId'
>;

const registrations = new Map<string, ClientEntityRegistration>();

interface RateBucket {
  count: number;
  windowStart: number;
}
const rateBuckets = new Map<string, RateBucket>();

export class ClientEntityNotRegisteredError extends Error {
  constructor(entityId: string) {
    super(
      `Client-side entity ${entityId} has no registered executor. ` +
        `Ensure it is registered at app startup ` +
        `(see client/src/lib/register-client-entities.ts).`,
    );
    this.name = 'ClientEntityNotRegisteredError';
  }
}

export class ClientEntityRateLimitError extends Error {
  constructor(entityId: string, limit: number) {
    super(`Rate limit exceeded for ${entityId}: max ${limit}/minute per (user, arche).`);
    this.name = 'ClientEntityRateLimitError';
  }
}

/**
 * Register an executor for a `executionMode: 'client-side'` entity along
 * with its rate-limit cap and plugin permission. Idempotent within a
 * session — re-registration replaces the prior registration (useful for
 * HMR; in production each executor module is imported exactly once).
 */
export function registerClientEntity<TInput, TResult>(
  entityId: string,
  registration: ClientEntityRegistration<TInput, TResult>,
): void {
  if (!Number.isInteger(registration.rateLimitPerMinute) || registration.rateLimitPerMinute <= 0) {
    throw new Error(
      `registerClientEntity(${entityId}): rateLimitPerMinute must be a positive integer`,
    );
  }
  if (!registration.pluginPermission) {
    throw new Error(`registerClientEntity(${entityId}): pluginPermission is required`);
  }
  registrations.set(entityId, registration as ClientEntityRegistration);
}

/**
 * Look up the registration for a client-side entity. Used by the bridge's
 * `client_entity_invoke` op to read the plugin permission + per-entity
 * rate-limit cap at request time.
 */
export function getClientEntityRegistration(
  entityId: string,
): ClientEntityRegistration | undefined {
  return registrations.get(entityId);
}

/**
 * Invoke a `executionMode: 'client-side'` entity by ID.
 *
 * Throws `ClientEntityNotRegisteredError` if no executor is registered for
 * the ID (catches the "framework wired but executor never imported" failure
 * at the call site rather than producing a confusing undefined result).
 *
 * Throws `ClientEntityRateLimitError` if the per-`(userId, archeId)` cap
 * for this entity has been exceeded in the current minute window.
 */
export async function invokeClientEntity<TInput, TResult>(
  entityId: string,
  input: TInput,
  context: ClientEntityContext,
): Promise<TResult> {
  const registration = registrations.get(entityId);
  if (!registration) {
    throw new ClientEntityNotRegisteredError(entityId);
  }

  const key = `${context.userId}::${context.archeId}::${entityId}`;
  const now = Date.now();
  const bucket = rateBuckets.get(key);
  if (!bucket || now - bucket.windowStart > 60_000) {
    rateBuckets.set(key, { count: 1, windowStart: now });
  } else if (bucket.count >= registration.rateLimitPerMinute) {
    throw new ClientEntityRateLimitError(entityId, registration.rateLimitPerMinute);
  } else {
    bucket.count++;
  }

  return registration.executor(input, context) as Promise<TResult>;
}

/**
 * Test-only: clear all registrations and rate-limit buckets so each test
 * starts from a clean slate. Not exported through any production surface.
 */
export function __resetClientEntityRegistryForTests(): void {
  registrations.clear();
  rateBuckets.clear();
}

/**
 * Test-only: enumerate registered entities for the architecture sync
 * trip-wire (`client-entity-registry-mirrors-entity-config.test.ts`).
 */
export function __getClientEntityRegistrationsForTests(): ReadonlyMap<
  string,
  ClientEntityRegistration
> {
  return registrations;
}
